/**
 * KBReviewModal.tsx — AI-validator contradiction review (Human-in-the-Loop).
 * Side-by-side new-vs-existing snippets + AI reasoning + 4 decisions
 * (accept / keep existing / merge / ask clarification). Ported from prototype.
 */
import { useState } from "react";

import { I } from "../../components/icons";
import type { KbDecision, KbReview } from "../../lib/mock";
import { ConfidenceBar, DomainPill, REC_LABEL, SnippetPanel, kbConfTone } from "./kbUi";

interface KBReviewModalProps {
  review: KbReview;
  onClose: () => void;
  onDecide: (id: string, decision: KbDecision, toastMsg: string) => void;
}

export function KBReviewModal({ review: r, onClose, onDecide }: KBReviewModalProps): JSX.Element {
  const [clarify, setClarify] = useState(false);
  const newName = r.newDocSource;
  const existingName = r.existingDocSource;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,22,28,0.45)",
        zIndex: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="fade-in"
        style={{
          width: 920,
          maxWidth: "100%",
          maxHeight: "85vh",
          background: "var(--c-bg-elev)",
          borderRadius: 12,
          boxShadow: "var(--sh-pop)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid var(--c-line)",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <I.Alert size={16} style={{ color: "var(--c-warn)", flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 13.5, fontWeight: 600 }}>
                Revisione contraddizione · <span className="t-mono">{r.id}</span>
              </span>
              <DomainPill domainKey={r.domain} />
              <span style={{ fontSize: 12, color: kbConfTone(r.confidence), fontWeight: 600 }}>
                {Math.round(r.confidence * 100)}% confidenza
              </span>
            </div>
            <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", marginTop: 2 }}>
              Caricato da {r.uploader} · {r.uploadedAt}
            </div>
          </div>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>
            <I.X size={14} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 18 }}>
          <div style={{ display: "flex", gap: 14, alignItems: "stretch", marginBottom: 18 }}>
            <SnippetPanel
              tone="new"
              eyebrow={`Nuovo · ${newName}`}
              docId={r.newDocId}
              source={r.newDocSource}
              text={r.newSnippet}
            />
            <SnippetPanel
              tone="existing"
              eyebrow={existingName ? `Esistente · ${existingName}` : "Esistente"}
              docId={r.existingDocId}
              source={r.existingDocSource}
              text={r.existingSnippet}
            />
          </div>

          <div
            style={{ border: "1px solid var(--c-ai-soft)", borderRadius: 10, overflow: "hidden" }}
          >
            <div
              style={{
                padding: "8px 14px",
                background: "var(--c-ai-soft)",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <I.Sparkle size={13} style={{ color: "var(--c-ai)" }} />
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "var(--c-ai)",
                }}
              >
                Analisi AI
              </span>
            </div>
            <div style={{ padding: "14px 16px" }}>
              <p
                style={{
                  margin: "0 0 14px",
                  fontSize: 13,
                  lineHeight: 1.65,
                  color: "var(--c-ink)",
                }}
              >
                {r.aiReasoning}
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 12, color: "var(--c-ink-3)" }}>Confidenza</span>
                  <ConfidenceBar value={r.confidence} />
                </div>
                <div style={{ fontSize: 12, color: "var(--c-ink-3)" }}>
                  Raccomandazione AI:{" "}
                  <span className="pill pill-ai" style={{ marginLeft: 4 }}>
                    {REC_LABEL[r.aiRecommendation]}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {clarify && (
            <div
              className="fade-in"
              style={{
                marginTop: 16,
                border: "1px solid var(--c-line-strong)",
                borderRadius: 10,
                padding: 14,
                background: "var(--c-bg)",
              }}
            >
              <div
                style={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  marginBottom: 8,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <I.Mail size={13} style={{ color: "var(--c-ink-3)" }} /> Richiesta di chiarimento a{" "}
                {r.uploader}
              </div>
              <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: "var(--c-ink-2)" }}>
                La revisione di “{newName}” vs “{existingName ?? "documento esistente"}” verrà
                segnata come <strong>chiarimento richiesto</strong>. L&apos;invio automatico del
                messaggio a {r.uploader} è in arrivo — per ora contattalo direttamente.
              </p>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
                <button className="btn btn-sm" onClick={() => setClarify(false)}>
                  Annulla
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() =>
                    onDecide(
                      r.id,
                      "clarification",
                      `Segnato come "chiarimento richiesto". Contatta ${r.uploader} direttamente — l'invio automatico del messaggio è in arrivo.`,
                    )
                  }
                >
                  <I.Check size={12} /> Conferma
                </button>
              </div>
            </div>
          )}
        </div>

        {!clarify && (
          <div
            style={{
              padding: "12px 18px",
              borderTop: "1px solid var(--c-line)",
              background: "var(--c-bg)",
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <button
              className="btn btn-primary btn-sm"
              style={{ background: "var(--c-ok)", borderColor: "var(--c-ok)" }}
              onClick={() =>
                onDecide(
                  r.id,
                  "accept_new",
                  `Documento accettato. ${r.newDocId} ora disponibile per query AI.`,
                )
              }
            >
              <I.Check size={13} /> Accetta nuovo
            </button>
            <button
              className="btn btn-sm"
              onClick={() =>
                onDecide(
                  r.id,
                  "reject_new",
                  `Documento rifiutato. ${r.existingDocId ?? "Il documento esistente"} mantiene la posizione.`,
                )
              }
            >
              Mantieni esistente
            </button>
            <button
              className="btn btn-sm"
              onClick={() =>
                onDecide(r.id, "merge", "Documenti contrassegnati per unione manuale.")
              }
            >
              <I.Layers size={13} /> Unisci
            </button>
            <button className="btn btn-sm" onClick={() => setClarify(true)}>
              <I.Mail size={13} /> Chiedi chiarimento
            </button>
            <div style={{ flex: 1 }} />
            <button className="btn btn-ghost btn-sm" onClick={onClose}>
              Annulla
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
